"use client";
import Form from "react-bootstrap/Form";
import { usePowerSearch } from "../contexts/power-search-context";
import Button from "react-bootstrap/Button";
import { useState } from "react";
import { useSelector } from "react-redux";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import {
  POWER_SEARCH_EVENT_NAMES,
  MIXPANEL_PROPS,
} from "shared/trackEvents/constants";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const CandidateSourceOptionsFilter = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const currentUser = useSelector((state) => state.users.user);

  const {
    filterOptions,
    candidateSource: selectedCandidateSource,
    sourceTypes,
    directorInLast,
    executiveInLast,
    updateFilter,
    updateFilterArray,
  } = usePowerSearch();

  const filteredOptions = (filterOptions.candidate_source_options || []).filter(
    (opt) => opt.value !== "all"
  );

  const aspiringDirectorOption = {
    value: "future_director",
    title: "OpenDirector Subscribers",
  };

  const allOptions = [...filteredOptions, aspiringDirectorOption];

  const isAllSelected = selectedCandidateSource.length === allOptions.length;

  const isChecked = (value) => {
    if (value === "future_director") {
      return sourceTypes.includes("future_director");
    }
    return selectedCandidateSource.includes(value);
  };

  const handleCandidateSourceChange = (e) => {
    const { value, checked } = e.target;
    let finalValue = [...selectedCandidateSource];

    if (checked) {
      if (!finalValue.includes(value)) finalValue.push(value);

      // Track candidate status option click
      const eventName = isOpenRemunerationPlan(currentUser)
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_CANDIDATE_STATUS_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_CANDIDATE_STATUS_CLICKED;

      mixpanelTrack(eventName, {
        [MIXPANEL_PROPS.FILTER_OPTION]: value,
        userEmail: currentUser?.email,
      });
    } else {
      finalValue = finalValue.filter((item) => item !== value);
    }

    updateFilter("candidateSource", finalValue);

    if (value === "future_director") {
      let newSourceTypes = [...sourceTypes];
      if (checked) {
        if (!newSourceTypes.includes("future_director")) {
          newSourceTypes.push("future_director");
        }
      } else {
        newSourceTypes = newSourceTypes.filter(
          (item) => item !== "future_director"
        );
      }

      updateFilter("sourceTypes", newSourceTypes);
    }
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (isAllSelected) {
      updateFilterArray("candidateSource", []);
    } else {
      updateFilterArray(
        "candidateSource",
        allOptions.map((option) => option.value)
      );
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between">
        <div
          className="d-flex align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <Button
            variant="link"
            size="sm"
            style={{
              display: "inline-block",
              padding: 0,
              margin: 0,
              textDecoration: "none",
              marginRight: "4px",
              focus: "none",
              outline: "none",
              border: "none",
              boxShadow: "none",
              fontWeight: "bold",
            }}
          >
            {isExpanded ? "−" : "+"}
          </Button>
          <p
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              margin: 0,
            }}
          >
            CANDIDATE STATUS
          </p>
        </div>
        <Button
          variant="link"
          size="sm"
          style={{ padding: 0 }}
          onClick={handleSelectAll}
        >
          {isAllSelected ? "Deselect All" : "Select All"}
        </Button>
      </div>
      <div className={`${isExpanded ? "d-block" : "d-none"}`}>
        {allOptions.map((option) => (
          <div
            key={option.value}
            className="d-flex col-gap-1 align-items-start mb-1"
          >
            <div
              key={option.value}
              className="d-flex col-gap-1 align-items-center mb-1"
            >
              <div>
                <input
                  type="checkbox"
                  id={`candidate-source-type-${option.value}`}
                  name="candidate-source-type"
                  value={option.value}
                  checked={isChecked(option.value)}
                  onChange={handleCandidateSourceChange}
                />
                <label
                  htmlFor={`candidate-source-type-${option.value}`}
                ></label>
              </div>
              <label
                htmlFor={`candidate-source-type-${option.value}`}
                className="mb-0 ml-2"
                style={{ flex: 1, fontSize: "13px" }}
              >
                {option.value === "future_director" ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="future-director-tooltip">
                        Verified profiles of aspiring directors who are open to
                        work
                      </Tooltip>
                    }
                  >
                    <span
                      style={{
                        cursor: "help",
                        borderBottom: "1px dotted #666",
                      }}
                    >
                      {option.title}
                    </span>
                  </OverlayTrigger>
                ) : option.value === "aspiring_director" ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="aspiring-director-tooltip">
                        OpenDirector verified profiles of aspiring directors
                      </Tooltip>
                    }
                  >
                    <span
                      style={{
                        cursor: "help",
                        borderBottom: "1px dotted #666",
                      }}
                    >
                      {option.title}
                    </span>
                  </OverlayTrigger>
                ) : (
                  option.title
                )}
                {option.value === "director_in_last" && (
                  <div className="d-inline-flex align-items-center ms-2">
                    <Form.Control
                      type="number"
                      min={1}
                      max={90}
                      style={{
                        width: "3.5rem",
                        height: "1.8rem",
                        fontSize: "0.8rem",
                      }}
                      className="mx-1 form-control-sm"
                      step="1"
                      value={directorInLast}
                      onChange={(e) => {
                        let value = Number.parseInt(e.target.value, 10) || 1;
                        if (value > 90) value = 90;
                        if (value < 1) value = 1;
                        return updateFilter("directorInLast", value);
                      }}
                    />
                    yrs
                  </div>
                )}
                {option.value === "executive_in_last" && (
                  <div className="d-inline-flex align-items-center ms-2">
                    <Form.Control
                      type="number"
                      min={1}
                      max={90}
                      style={{
                        width: "3.5rem",
                        height: "1.8rem",
                        fontSize: "0.8rem",
                      }}
                      className="mx-1 form-control-sm"
                      step="1"
                      value={executiveInLast}
                      onChange={(e) => {
                        let value = Number.parseInt(e.target.value, 10) || 1;
                        if (value > 90) value = 90;
                        if (value < 1) value = 1;
                        return updateFilter("executiveInLast", value);
                      }}
                    />
                    yrs
                  </div>
                )}
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CandidateSourceOptionsFilter;
